import { FC, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useTranslation } from 'react-i18next';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';

interface IProps {
  methods: any;
  data: any;
  onSubmit: (data: any) => void;
  reportParams?: any;
}
const ReportForm: FC<IProps> = ({ methods, data, onSubmit }) => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState(data?.user || []);

  useEffect(() => {
    methods.setValue('date_from', new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    methods.setValue('date_to', new Date());
  }, []);

  const groups = methods.watch('group_id');

  useEffect(() => {
    const groupIds = groups?.map((gr: any) => gr.value);

    /**
     * When a group is selected, filter employee options
     * to only employees from groups selected and
     * automatically select all employees
     *
     * If no group selected - clear selected employees
     */
    if (groupIds) {
      const filteredEmployees = data?.user.filter((empl: any) => {
        /**
         * If employee doesn't have any groups check if "No group"
         * is selected which value is set to 0 from BE
         */
        if (!empl.groups.length) return groupIds.includes('null');

        const isInSelectedGroups = empl.groups.reduce((flag: boolean, currGr: number) => {
          return flag || groupIds.includes(currGr);
        }, false);
        return isInSelectedGroups;
      });

      setEmployees(filteredEmployees);
      methods.setValue('user_id', filteredEmployees);
    } else {
      setEmployees(data?.user);
      methods.setValue('user_id', null);
    }
  }, [groups]);

  return (
    <Form
      methods={methods}
      btnLabel={t('reports.downloadLeavesReport')}
      hideCancelBtn
      onSubmit={onSubmit}
    >
      <Row>
        <Col
          lg={{ span: 6 }}
          xl={{
            offset: 2,
            span: 4,
          }}
        >
          <FormGroup
            controlId="formGroupGroups"
            label={t('reports.groups')}
            type="select"
            as="select"
            name="group_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            selectOptions={data?.group}
          />
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={4}
        >
          <FormGroup
            controlId="formGroupEmployees"
            label={t('reports.employees')}
            type="select"
            as="select"
            name="user_id"
            isMulti
            isClearable
            isSearchable
            allowSelectAll
            showTotal
            selectOptions={employees}
            limitOptions
          />
        </Col>
      </Row>
      <Row>
        <Col
          xl={{
            offset: 2,
            span: 4,
          }}
        >
          <Row>
            <Col>
              <FormGroup
                controlId="formGroupFromDate"
                label={t('reports.fromDate')}
                type="datepicker"
                name="date_from"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
            <Col>
              <FormGroup
                controlId="formGroupToDate"
                label={t('reports.toDate')}
                type="datepicker"
                name="date_to"
                dateFormat="dd.MM.yyyy"
                showMonthDropdown
                showYearDropdown
                isRequired
              />
            </Col>
          </Row>
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={2}
        >
          <FormGroup
            controlId="formGroupLeaveType"
            label={t('reports.leaveType')}
            type="select"
            as="select"
            name="type"
            isMulti
            isClearable
            isRequired
            isSearchable
            allowSelectAll
            selectOptions={data?.type}
          />
        </Col>
        <Col
          lg={{ span: 6 }}
          xl={2}
        >
          <FormGroup
            controlId="formGroupLeaveStatus"
            label={t('reports.leaveStatus')}
            type="select"
            as="select"
            name="status"
            isMulti
            isClearable
            isRequired
            isSearchable
            allowSelectAll
            selectOptions={data?.status}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default ReportForm;
